import { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import { Link } from "react-router-dom";
import { Search, Crown, X, Contact } from "lucide-react";
import { api } from "../lib/api";
import { Shell } from "../components/Shell";
import { Avatar, Flag, RedFlagIcon } from "../components/Avatar";
import { ESTADOS, countryName, fmtRelativo, estadoLabel } from "../lib/data";

export default function Personas() {
  const [people, setPeople] = useState(null);
  const [meta, setMeta] = useState({ etiquetas: [], ciudades: [], paises: [] });
  const [q, setQ] = useState("");
  const [f, setF] = useState({ estado: "", coronada: false, red_flag: false, etiqueta: "", ciudad: "", pais: "" });

  const params = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (f.estado) p.estado = f.estado;
    if (f.coronada) p.coronada = true;
    if (f.red_flag) p.red_flag = true;
    if (f.etiqueta) p.etiqueta = f.etiqueta;
    if (f.ciudad) p.ciudad = f.ciudad;
    if (f.pais) p.pais = f.pais;
    return p;
  }, [q, f]);

  useEffect(() => { api.get("/people/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/people", { params }).then((r) => setPeople(r.data)).catch(() => setPeople([])), 200);
    return () => clearTimeout(t);
  }, [params]);

  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k] === v ? ((k === "coronada" || k === "red_flag") ? false : "") : v }));
  const hasFilters = Object.values(f).some(Boolean) || q;

  return (
    <Shell title={t("tabs.personas")} right={
      <Link to="/personas/nueva?importar=1" data-testid="people-import-btn" className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
        <Contact size={14} className="text-[#d4af37]" /> {t("person.import")} </Link>
    }>
      <div className="relative mb-3">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="people-search-input" className="field pl-11" placeholder={t("person.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1" data-testid="filter-chips">
        <button data-testid="filter-coronadas" onClick={() => toggle("coronada", true)} className={`chip ${f.coronada ? "chip-on" : ""}`}>
          <Crown size={14} /> {t("person.filterCrowned")} </button>
        <button data-testid="filter-red-flag" onClick={() => toggle("red_flag", true)} className={`chip ${f.red_flag ? "chip-on" : ""}`}>
          <RedFlagIcon size={14} testId="filter-red-flag-icon" /> {t("person.redFlag")} </button>
        {ESTADOS.map((e) => (
          <button key={e.value} data-testid={`filter-estado-${e.value}`} onClick={() => toggle("estado", e.value)} className={`chip ${f.estado === e.value ? "chip-on" : ""}`}>{e.label}</button>
        ))}
        {meta.etiquetas.map((et) => (
          <button key={et} data-testid={`filter-etiqueta-${et}`} onClick={() => toggle("etiqueta", et)} className={`chip ${f.etiqueta === et ? "chip-on" : ""}`}>#{et}</button>
        ))}
        {meta.ciudades.map((c) => (
          <button key={c} data-testid={`filter-ciudad-${c}`} onClick={() => toggle("ciudad", c)} className={`chip ${f.ciudad === c ? "chip-on" : ""}`}>{c}</button>
        ))}
        {meta.paises.map((p) => (
          <button key={p} data-testid={`filter-pais-${p}`} onClick={() => toggle("pais", p)} className={`chip ${f.pais === p ? "chip-on" : ""}`}><Flag iso={p} size={16} /> {countryName(p)}</button>
        ))}
        {hasFilters && (
          <button data-testid="filter-clear" onClick={() => { setQ(""); setF({ estado: "", coronada: false, red_flag: false, etiqueta: "", ciudad: "", pais: "" }); }} className="chip"><X size={14} /> {t("common.clear")}</button>
        )}
      </div>

      <p data-testid="people-count" className="text-xs text-muted-foreground mt-4 mb-2">{people ? t("person.count", { count: people.length }) : t("common.loading")}</p>

      <ul className="space-y-2" data-testid="people-list">
        {people?.map((p, i) => (
          <li key={p.id} className="fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <Link to={`/personas/${p.id}`} data-testid={`person-card-${p.id}`} className="surface p-3 flex items-center gap-3 hover:bg-secondary transition-colors active:scale-[0.99]">
              <Avatar nombre={p.nombre} src={p.foto_url} coronada={p.coronada?.activa} redFlag={p.red_flag?.activa} size={56} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span data-testid="person-card-name" className="font-medium truncate">{p.nombre}</span>
                  <Flag iso={p.pais_iso} size={18} />
                  {p.red_flag?.activa && <RedFlagIcon size={14} testId={`person-card-red-flag-${p.id}`} />}
                  {p.estado !== "activa" && <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5">{estadoLabel(p.estado)}</span>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{p.ciudad_vive?.nombre || t("person.noCity")}{p.apodo ? ` · "${p.apodo}"` : ""}</p>
                <p className="text-xs text-muted-foreground/70" data-testid="person-card-ultima">{t("common.lastTime", { v: fmtRelativo(p.ultima_fisica) })}</p>
              </div>
            </Link>
          </li>
        ))}
        {people && people.length === 0 && (
          <li data-testid="people-empty" className="surface p-8 text-center text-sm text-muted-foreground space-y-3">
            <p>{hasFilters ? t("person.noResults") : t("person.empty")}</p>
            {!hasFilters && (
              <Link to="/personas/nueva?importar=1" data-testid="people-empty-import-btn" className="btn-ghost inline-flex items-center gap-2 text-sm"><Contact size={16} /> {t("person.importPhone")}</Link>
            )}
          </li>
        )}
      </ul>
    </Shell>
  );
}
